
import { Component, Vue, Ref } from 'vue-property-decorator'
import UnorderedList from '@/components/molecules/UnorderedList.vue'
import ListItem from '@/components/molecules/ListItem.vue'
import LearningUnitMap from '@/components/templates/LearningUnitMap.vue'
import ModalSelectChart from '@/views/modal/ModalSelectChart.vue'
import { SubjectCode } from '@/const/SubjectCode'
import { SystemChartDetail, SystemChartUnit, SystemChart } from '@/models/SystemChart'
import { LearningProgress } from '@/const/LearningProgress'
import { SystemChartsApi } from '@/api/methods/SystemChartsApi'
import { CurriculumUnitKnowledgeMapsApi } from '@/api/methods/CurriculumUnitKnowledgeMapsApi'
import { ZnCurriculumUnitsApi } from '@/api/methods/ZnCurriculumUnitsApi'

/**
 * 知識MAP
 */
@Component({
  components: {
    UnorderedList,
    ListItem,
    LearningUnitMap,
    ModalSelectChart,
  },
})
export default class LearningMap extends Vue {
  /** 系統図選択モーダル */
  @Ref()
  modalSelectChart!: ModalSelectChart

  /** 選択中の教科コード */
  private currentSubjectCode: string = SubjectCode.LIST[0].code

  /** 選択中の系統図ID */
  private currentChartId: number = 0

  /** 系統図のリスト */
  private systemChartList: SystemChart[] = []

  /** 知識MAP詳細情報 */
  private systemChartDetail: SystemChartDetail | null = null

  /**
   * 選択中の系統図情報を引き当てる
   *
   * @returns 系統図情報(未選択などの場合はundefined)
   */
  private get currentChart(): SystemChart | undefined {
    return this.systemChartList.find((chart) => {
      return chart.id === this.currentChartId
    })
  }

  /**
   * 選択中の系統図名
   *
   * @returns 系統図名
   */
  private get currentChartName(): string {
    return this.currentChart?.name || ''
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // クッキーから教科情報を取得
    this.currentSubjectCode = await this.$appCookies.studentMenuState.subjectCode.get()

    // 系統図一覧の取得
    await this.loadCharts()

    // 系統図一覧が0件の場合は処理を終了
    if (this.systemChartList.length == 0) {
      // URLにクエリ情報が残っている場合はクリアする
      if (this.$route.query.systemChartId) {
        await this.$router.replace(this.$router.currentRoute.path)
      }
      this.$loading.complete()
      return
    }

    // クエリから系統図IDを取得
    const systemChartId = this.$route.query.systemChartId

    // 取得した教材一覧から、教材IDの配列を作成
    const idList = this.systemChartList.map((systemChart) => systemChart.id)

    if (systemChartId && idList.includes(Number(systemChartId))) {
      // クエリの教材IDを取得した場合の処理
      this.currentChartId = Number(systemChartId)
    } else {
      // クエリの教材ID未指定、もしくは教材一覧に該当の教材IDがない場合の処理
      this.currentChartId = this.systemChartList[0].id
      await this.$router.replace({
        query: { systemChartId: `${this.currentChartId}` },
      })
    }

    // 系統図の詳細情報取得
    await this.loadChartUnits()

    this.$loading.complete()
  }

  /**
   * 系統図一覧の更新
   */
  private async loadCharts(): Promise<void> {
    const ststemCharts = await SystemChartsApi.getSystemCharts(this.currentSubjectCode)
    this.systemChartList = ststemCharts.map((chart) => {
      return {
        id: chart.id,
        code: chart.code,
        name: chart.name,
        imageUrl: chart.imageUrl,
      }
    })

    // 選択中の系統図を変更する
    if (this.currentChart) {
      // 選択中の系統図が存在する場合は終了(選択状態を継続)
    } else if (this.systemChartList.length == 0) {
      // 系統図一覧が0件の場合は未選択状態に戻す
      this.currentChartId = 0
    } else {
      // 選択中の系統図が存在しない場合(一覧の内容が変更された場合)は先頭要素を初期選択値として設定する
      this.currentChartId = this.systemChartList[0].id
    }
  }

  /**
   * 系統図の単元情報取得
   */
  private async loadChartUnits(): Promise<void> {
    // 詳細情報をリセット
    this.systemChartDetail = null

    if (!this.currentChart) {
      // 系統図情報が未選択の場合は処理を終了(系統図が存在しない場合など)
      return
    }

    // 単元情報取得
    const curriculumUnitKnowledgeMaps = await CurriculumUnitKnowledgeMapsApi.getCurriculumUnitKnowledgeMaps(
      this.currentChartId
    )

    // 単元情報をフロント用に整形
    const units: SystemChartUnit[] = curriculumUnitKnowledgeMaps.flatMap((knowledgeCategory) => {
      return knowledgeCategory.units.map((unit) => {
        return {
          id: unit.id,
          name: unit.name,
          viewNumber: unit.viewNumber,
          colorCode: knowledgeCategory.colorCode,
          xPosition: unit.xPosition,
          yPosition: unit.yPosition,
          learningProgress: LearningProgress.findByCode(unit.progress),
        }
      })
    })

    // 知識マップ情報の設定
    this.systemChartDetail = {
      units: units,
      imageUrl: this.currentChart.imageUrl,
    }
  }

  /**
   * 系統図選択ボタン押下時のイベント
   */
  public onSelectChartButton(): void {
    // 系統図一覧が0件の場合は処理をキャンセル
    if (this.systemChartList.length == 0) {
      return
    }

    this.modalSelectChart.show(this.currentChartId)
  }

  /**
   * 系統図選択イベント
   *
   * @param id 選択中の系統図ID
   */
  private async eventSubmitSystemChart(id: number): Promise<void> {
    // 選択値が変わらない場合は終了
    if (this.currentChartId == id) {
      return
    }

    this.$loading.start()

    // 選択値を現在の系統図IDに設定
    this.currentChartId = id
    // クエリ情報の更新
    await this.$router.replace({
      query: { systemChartId: `${this.currentChartId}` },
    })

    // 系統図の詳細情報取得
    await this.loadChartUnits()

    this.$loading.complete()
  }

  /**
   * 単元選択ボタン押下時のイベント
   *
   * @param id 増進堂単元ID
   */
  public async onSelectUnit(id: number): Promise<void> {
    const znUnitConvert = await ZnCurriculumUnitsApi.getZnCurriculumUnitConvert(id)

    // TODO: 以下、一部挙動確認用の処理なので、遷移先画面実装後に差し替え
    const unitIds = znUnitConvert.units.map((unit) => {
      return unit.id
    })

    if (znUnitConvert.units.length > 1) {
      // 複数の日能研単元に紐付くケース
      this.$router.push({
        path: `/student/map/${znUnitConvert.znUnit.id}`,
        query: { systemChartId: `${this.currentChartId}` },
      })
    } else {
      // 単独の日能研単元に紐付くケース
      this.$router.push({
        path: `/student/general/${unitIds[0]}`,
        query: { systemChartId: `${this.currentChartId}` },
      })
    }
  }
}
